import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents, VariantSelectedEvent } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {Map<string, Document>} */
  #cachedDocs = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();
  /** @type {Promise<Document|null> | null} */
  #pendingPrefetch = null;

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState);
  }

  #handleCartUpdate = () => {
    this.#cachedContent.clear();
    this.#cachedDocs.clear();
  };

  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;

    const variantPicker = modalContent.querySelector('variant-picker');

    if (!variantPicker || typeof variantPicker.updateVariantPicker !== 'function') {
      return;
    }

    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Dynamically load bundle-grind-selectors.js if it hasn't been loaded yet.
   * Extracts the script URL from the fetched PDP HTML.
   */
  async #ensureBundleGrindScript(fetchedDoc) {
    if (typeof window.refreshBundleGrindSelectors === 'function') return;
    if (!fetchedDoc) return;

    const scriptTag = fetchedDoc.querySelector('script[src*="bundle-grind-selectors"]');
    if (!scriptTag?.src) return;

    return new Promise((resolve) => {
      const s = document.createElement('script');
      s.src = scriptTag.src;
      s.onload = resolve;
      s.onerror = resolve;
      document.head.appendChild(s);
    });
  }

  /**
   * Prefetch the product page on hover so the modal opens instantly on click.
   * Called from product-card.js on pointerenter/focusin.
   */
  prefetch = async () => {
    const currentUrl = this.productPageUrl;
    if (!currentUrl || this.#cachedContent.has(currentUrl)) return;
    // If already prefetching this URL, don't start another request
    if (this.#pendingPrefetch) return;

    this.#pendingPrefetch = this.fetchProductPage(currentUrl);
    const fetchedDoc = await this.#pendingPrefetch;
    this.#pendingPrefetch = null;

    if (fetchedDoc) {
      // Cache the parsed document for the bundle-grind script lookup
      this.#cachedDocs.set(currentUrl, fetchedDoc);

      const gridElement = fetchedDoc.querySelector('[data-product-grid-content]');
      if (gridElement) {
        this.#cachedContent.set(currentUrl, /** @type {Element} */ (gridElement.cloneNode(true)));
      }

      // Preload bundle-grind-selectors script so it's ready when the modal opens
      this.#ensureBundleGrindScript(fetchedDoc);
    }
  };

  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;

    // Wait for any in-flight prefetch to complete before checking cache
    if (this.#pendingPrefetch) {
      await this.#pendingPrefetch;
    }

    let productGrid = this.#cachedContent.get(currentUrl);
    let fetchedDoc = this.#cachedDocs.get(currentUrl) || null;

    if (!productGrid) {
      fetchedDoc = await this.fetchProductPage(currentUrl);
      if (fetchedDoc) {
        this.#cachedDocs.set(currentUrl, fetchedDoc);
        const gridElement = fetchedDoc.querySelector('[data-product-grid-content]');
        if (gridElement) {
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          this.#cachedContent.set(currentUrl, productGrid);
        }
      }
    }

    if (productGrid) {
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
      this.#updateVariantPicker(productGrid);

      // Ensure bundle grind script is loaded (use cached doc, no re-fetch)
      if (typeof window.refreshBundleGrindSelectors !== 'function' && fetchedDoc) {
        await this.#ensureBundleGrindScript(fetchedDoc);
      }

      const modalContent = document.getElementById('quick-add-modal-content');
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (modalContent && typeof window.refreshBundleGrindSelectors === 'function') {
            window.refreshBundleGrindSelectors(modalContent);
          }
        });
      });
    }

    this.#openQuickAddModal();
  };

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);
    dialogComponent.showDialog();

    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);

      if (productPrice) {
        productHeader.appendChild(productPrice);
      }

      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }

      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }

      productDetails?.remove();
    }

    morph(modalContent, productGrid);
    this.#syncVariantSelection(modalContent);
  }

  #updateQuickAddButtonState = (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;

    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  };

  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');

    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink, {
      signal: this.#abortController.signal,
    });
    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, {
      signal: this.#abortController.signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (event) => {
    const anchorElement = event.detail.data.html?.querySelector('.view-product-title a');
    const viewMoreDetailsLink = this.querySelector('.view-product-title a');
    const mobileProductTitle = this.querySelector('.product-header a');

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();

    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}